import { useState, useEffect } from 'react';
import { api } from '../../../api/client';
import {
  Award, Star, BarChart3, TrendingUp, Package, ShoppingCart, Globe, Eye,
  CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';

interface PerformanceTabProps {
  product: any;
  sellerId: number;
}

const SITE_FLAGS: Record<string, string> = {
  MLA: '🇦🇷', MLB: '🇧🇷', MLM: '🇲🇽', MLC: '🇨🇱', MCO: '🇨🇴', MLU: '🇺🇾',
};

const LEVEL_COLORS: Record<string, string> = {
  professional: 'var(--color-success)',
  high: 'var(--color-success)',
  standard: 'var(--color-primary)',
  medium: 'var(--color-primary)',
  basic: 'var(--color-warning)',
  low: 'var(--color-error)',
};

const LEVEL_BG: Record<string, string> = {
  professional: 'rgba(34,197,94,0.1)',
  high: 'rgba(34,197,94,0.1)',
  standard: 'rgba(59,130,246,0.1)',
  medium: 'rgba(59,130,246,0.1)',
  basic: 'rgba(234,179,8,0.1)',
  low: 'rgba(239,68,68,0.1)',
};

function getScoreColor(score: number): string {
  if (score >= 80) return 'var(--color-success)';
  if (score >= 50) return 'var(--color-warning)';
  return 'var(--color-error)';
}

function BucketStatusIcon({ status }: { status: string }) {
  const s = status?.toLowerCase() || '';
  if (s === 'good' || s === 'optimal' || s === 'completed') {
    return <CheckCircle size={16} className="flex-shrink-0" style={{ color: 'var(--color-success)' }} />;
  }
  if (s === 'bad' || s === 'critical') {
    return <XCircle size={16} className="flex-shrink-0" style={{ color: 'var(--color-error)' }} />;
  }
  return <AlertTriangle size={16} className="flex-shrink-0" style={{ color: 'var(--color-warning)' }} />;
}

export default function PerformanceTab({ product, sellerId }: PerformanceTabProps) {
  const [data, setData] = useState<{ performances: any[]; visits: Record<string, any> } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get<any>(`/catalog/products/${product.item_id}/performance?seller_id=${sellerId}`);
        if (res.success && res.data) {
          setData(res.data);
        } else {
          setError((res as any).error || 'No disponible');
        }
      } catch {
        setError('Error al cargar datos de performance');
      }
      setLoading(false);
    };
    load();
  }, [product.item_id, sellerId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-8 h-8 border-3 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const performances = data?.performances || [];
  const visits = data?.visits || {};

  const productMetrics = [
    { label: 'Stock', value: product.available_quantity ?? 0, icon: Package, color: 'var(--color-primary)' },
    { label: 'Vendidos', value: product.sold_quantity ?? 0, icon: ShoppingCart, color: 'var(--color-success)' },
    { label: 'Sites', value: product.site_count ?? 0, icon: Globe, color: 'var(--color-primary)' },
    { label: 'Rating', value: product.amazon_rating != null ? Number(product.amazon_rating).toFixed(1) : '—', icon: Star, color: 'var(--color-warning)' },
    { label: 'Reviews', value: product.amazon_ratings_total != null ? Number(product.amazon_ratings_total).toLocaleString() : '—', icon: BarChart3, color: 'var(--color-primary)' },
    { label: 'Vendidos/mes (AMZ)', value: product.keepa_monthly_sold != null ? Number(product.keepa_monthly_sold).toLocaleString() : '—', icon: TrendingUp, color: 'var(--color-success)' },
  ];

  // Calculate total visits across all sites
  const totalVisits = Object.values(visits).reduce((sum: number, v: any) => sum + (v.total_visits || 0), 0);

  return (
    <div className="space-y-6">
      {/* Product Metrics */}
      <div>
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
          <BarChart3 size={16} />
          Métricas del Producto
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
          {productMetrics.map((m) => {
            const Icon = m.icon;
            return (
              <div key={m.label} className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon size={14} style={{ color: m.color }} />
                  <span className="text-xs text-[var(--color-text-muted)]">{m.label}</span>
                </div>
                <div className="text-lg font-semibold text-[var(--color-text)]">{m.value}</div>
              </div>
            );
          })}
          <div className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
            <div className="flex items-center gap-1.5 mb-1">
              <Eye size={14} style={{ color: 'var(--color-primary)' }} />
              <span className="text-xs text-[var(--color-text-muted)]">Visitas 30d</span>
            </div>
            <div className="text-lg font-semibold text-[var(--color-text)]">{totalVisits.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Visits by site */}
      {Object.keys(visits).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
            <Eye size={16} />
            Visitas por Marketplace (últimos 30 días)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(visits).map(([siteId, v]: [string, any]) => (
              <div key={siteId} className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)] text-center">
                <div className="text-lg mb-1">{SITE_FLAGS[siteId] || '🌐'}</div>
                <div className="text-xs font-medium text-[var(--color-text-muted)]">{siteId}</div>
                <div className="text-xl font-bold text-[var(--color-text)] mt-1">{(v.total_visits || 0).toLocaleString()}</div>
                <div className="text-xs text-[var(--color-text-muted)]">visitas</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quality Score per site */}
      {error ? (
        <div className="p-4 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)] text-center">
          <Award size={32} className="mx-auto mb-2" style={{ color: 'var(--color-text-muted)' }} />
          <p className="text-sm text-[var(--color-text-muted)]">Quality Score no disponible</p>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">{error}</p>
        </div>
      ) : performances.length === 0 ? (
        <div className="p-4 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)] text-center">
          <Award size={32} className="mx-auto mb-2" style={{ color: 'var(--color-text-muted)' }} />
          <p className="text-sm text-[var(--color-text-muted)]">Sin datos de performance disponibles</p>
        </div>
      ) : (
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
            <Award size={16} />
            Quality Score por Marketplace
          </h3>
          <div className="space-y-4">
            {performances.map((perf: any) => {
              const level = (perf.level || '').toLowerCase();
              return (
                <div key={perf.site_item_id} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] overflow-hidden">
                  {/* Site header */}
                  <div className="flex items-center gap-3 p-4 border-b border-[var(--color-border-light)]">
                    <span className="text-xl">{SITE_FLAGS[perf.site_id] || '🌐'}</span>
                    <div className="flex-1">
                      <span className="text-sm font-medium text-[var(--color-text)]">{perf.site_id}</span>
                      <span className="text-xs text-[var(--color-text-muted)] ml-2 font-mono">{perf.site_item_id}</span>
                    </div>
                    <div className="text-3xl font-bold" style={{ color: getScoreColor(perf.score ?? 0) }}>
                      {perf.score ?? '—'}
                    </div>
                    <span
                      className="px-3 py-1 rounded-full text-xs font-medium"
                      style={{
                        color: LEVEL_COLORS[level] || 'var(--color-text-muted)',
                        backgroundColor: LEVEL_BG[level] || 'var(--color-surface-hover)',
                      }}
                    >
                      {perf.level || 'Sin nivel'}
                    </span>
                  </div>

                  {/* Buckets */}
                  {perf.buckets && perf.buckets.length > 0 && (
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {perf.buckets.map((bucket: any) => (
                        <div key={bucket.key} className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-surface)]">
                          <div className="flex items-center gap-2 mb-2">
                            <BucketStatusIcon status={bucket.status} />
                            <span className="text-xs font-medium text-[var(--color-text)]">
                              {bucket.title || bucket.key?.replace(/_/g, ' ') || 'Bucket'}
                            </span>
                            {bucket.score != null && (
                              <span className="ml-auto text-xs font-semibold" style={{ color: getScoreColor(bucket.score) }}>
                                {Math.round(bucket.score)}
                              </span>
                            )}
                          </div>
                          {bucket.variables && bucket.variables.length > 0 && (
                            <div className="space-y-1">
                              {bucket.variables.map((v: any, i: number) => (
                                <div key={i} className="flex items-center justify-between text-xs">
                                  <span className="text-[var(--color-text-muted)] truncate mr-2">
                                    {v.title || v.key?.replace(/_/g, ' ') || `Variable ${i + 1}`}
                                  </span>
                                  <BucketStatusIcon status={v.status} />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
